export interface CanvasOverlaySnapshot {
  /** Pixi overlays open over the game right now: a big win, a feature trigger or finished panel, the gamble, the transition. */
  open: number;
}

/**
 * How many Pixi overlays are open over the game. The DOM chrome that must not float over them (the
 * control bar family) hides while the count is above zero — the overlap that step 12 solved by
 * z-order is solved by hiding (decision 18). Every presenter that opens a canvas overlay marks it
 * here around its panel's lifetime; the returned release is idempotent.
 */
export class CanvasOverlayStore {
  private count = 0;
  private readonly listeners = new Set<(snapshot: CanvasOverlaySnapshot) => void>();

  public getSnapshot(): CanvasOverlaySnapshot {
    return { open: this.count };
  }

  /** Marks one overlay open; call the returned function once it has closed. */
  public open(): () => void {
    this.count += 1;
    this.emit();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.count -= 1;
      this.emit();
    };
  }

  public subscribe(listener: (snapshot: CanvasOverlaySnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}
